import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from '@app/auth/_services/auth.service';
import { environment } from '@environments/environment';

type ClientErrorType = 'angular' | 'window' | 'unhandledrejection';

export interface ClientErrorPayload {
  time: string;
  type: ClientErrorType;
  message: string;
  url: string;
  route: string;
  userAgent: string;
  stack?: string;
  extra?: Record<string, unknown>;
  userId?: string | number;
  env?: string;
}

@Injectable({ providedIn: 'root' })
export class ClientErrorReporterService {
  private readonly TELEGRAM_API = 'https://api.telegram.org';
  private readonly MAX_MESSAGE_LENGTH = 3900;

  private installed = false;
  private sentTimestamps: number[] = [];
  private readonly maxPerMinute = 3;
  private readonly windowMs = 60_000;

  constructor(
    private authService: AuthService,
    private router: Router
  ) {}

  installGlobalListeners() {
    if (this.installed) {
      return;
    }

    window.addEventListener('error', (event: ErrorEvent) => {
      const message = event.message || 'Unknown window error';
      const stack = event.error?.stack;
      this.report({
        type: 'window',
        message,
        stack,
        extra: {
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno,
        },
        raw: event.error
      });
    });

    window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
      const reason = event.reason;
      const message = this.normalizeMessage(reason);
      const stack = this.normalizeStack(reason);
      this.report({
        type: 'unhandledrejection',
        message,
        stack,
        extra: {
          reason: this.safeJson(reason)
        },
        raw: reason
      });
    });

    this.installed = true;
  }

  reportAngularError(error: any) {
    const original = error?.originalError || error?.ngOriginalError || error;
    const message = this.normalizeMessage(original);
    const stack = this.normalizeStack(original);
    this.report({
      type: 'angular',
      message,
      stack,
      extra: {
        original: this.safeJson(original)
      },
      raw: original
    });
  }

  private report(
    partial: Omit<ClientErrorPayload, 'time' | 'url' | 'route' | 'userAgent'> & { raw?: any }
  ) {
    if (this.shouldIgnore(partial.raw)) {
      return;
    }
    if (!this.canSend()) {
      return;
    }

    const payload: ClientErrorPayload = {
      time: new Date().toISOString(),
      type: partial.type,
      message: partial.message,
      stack: partial.stack,
      extra: partial.extra,
      url: window.location.href,
      route: this.router.url,
      userAgent: navigator.userAgent,
      userId: this.authService.currentUserValue?.id,
      env: environment.production ? 'prod' : 'dev'
    };

    const token = environment.telegramBotToken;
    const chatIdRaw = environment.telegramChatId;
    if (environment.production && token && chatIdRaw) {
      this.sendTelegramMessage(payload, token, chatIdRaw);
      return;
    }
  }

  private sendTelegramMessage(payload: ClientErrorPayload, token: string, chatIdRaw: string) {
    const text = this.formatMessage(payload);
    const chatIds = chatIdRaw
      .split(',')
      .map((id) => id.trim())
      .filter(Boolean);

    chatIds.forEach((chatId) => {
      const url = `${this.TELEGRAM_API}/bot${encodeURIComponent(token)}/sendMessage` +
        `?chat_id=${encodeURIComponent(chatId)}` +
        `&text=${encodeURIComponent(text)}` +
        `&disable_web_page_preview=true`;

      try {
        fetch(url, { method: 'GET', mode: 'no-cors' });
      } catch (_error) {
        // Intentionally ignored to avoid infinite error loops
      }
    });
  }

  private formatMessage(payload: ClientErrorPayload) {
    const lines = [
      'Client error',
      `time: ${payload.time || new Date().toISOString()}`,
      `type: ${payload.type || 'error'}`,
      `message: ${payload.message || 'unknown'}`,
      payload.url ? `url: ${payload.url}` : null,
      payload.route ? `route: ${payload.route}` : null,
      payload.userAgent ? `ua: ${payload.userAgent}` : null,
      payload.userId ? `userId: ${payload.userId}` : null,
      payload.stack ? `stack: ${payload.stack}` : null,
      payload.extra ? `extra: ${this.safeJson(payload.extra)}` : null,
    ].filter(Boolean);

    return lines.join('\n').slice(0, this.MAX_MESSAGE_LENGTH);
  }

  private canSend() {
    const now = Date.now();
    this.sentTimestamps = this.sentTimestamps.filter(ts => now - ts < this.windowMs);
    if (this.sentTimestamps.length >= this.maxPerMinute) {
      return false;
    }
    this.sentTimestamps.push(now);
    return true;
  }

  private shouldIgnore(raw: any) {
    if (!raw) {
      return false;
    }
    const status =
      raw?.status ??
      raw?.error?.status ??
      raw?.rejection?.status ??
      raw?.originalError?.status ??
      raw?.ngOriginalError?.status;
    return status === 401 || status === 403;
  }

  private normalizeMessage(value: any) {
    if (!value) {
      return 'Unknown error';
    }
    if (typeof value === 'string') {
      return value;
    }
    if (value?.message) {
      return value.message;
    }
    try {
      return JSON.stringify(value);
    } catch (_error) {
      return String(value);
    }
  }

  private normalizeStack(value: any) {
    if (!value) {
      return undefined;
    }
    if (typeof value === 'string') {
      return value;
    }
    return value?.stack;
  }

  private safeJson(value: any, max = 1200) {
    try {
      const text = JSON.stringify(value, null, 2);
      return text.length > max ? `${text.slice(0, max)}…` : text;
    } catch (_error) {
      return String(value);
    }
  }
}
